import logger from './logger';

// Default backoff constants for queryWithRetry.
//
// WHY delay at all?
//   When a server fails due to overload (e.g. historian 429 / 5xx), retrying
//   immediately adds more traffic to an already struggling backend. The delay
//   gives it a moment to recover. Without it, retries accelerate the failure.
//
// WHY jitter (random)?
//   If many concurrent requests all fail at the same moment, a fixed delay
//   causes them all to retry at the same moment — a "thundering herd". Adding
//   randomness smears retries across a window, reducing peak load.
//
// WHY exponential?
//   Each successive failure suggests the backend needs more time to recover.
//   Doubling the window per attempt backs off progressively rather than
//   hammering at a fixed rate.
//
// Values chosen:
//   baseDelayMs=500 — first retry waits 0–500 ms; fast enough not to feel broken.
//   maxDelayMs=8000 — cap at 8 s so the UI never feels completely frozen.
const DEFAULT_BASE_DELAY_MS = 500;
const DEFAULT_MAX_DELAY_MS = 8000;

/**
 * Classify whether an error is retryable.
 *
 * Retry on:
 *   - request timeout (`error.message === 'Request timed out'`)
 *   - transport failure (`status === 0`), set on unreachable-API / `fetch`
 *     rejections in `environment.js`
 *   - HTTP 429 (rate-limited / overloaded), which benefits from the
 *     existing exponential backoff between attempts
 *   - HTTP 500–599 (server error), when a numeric status is present on the
 *     error
 *   - unknown errors (default = retryable), preserving existing caller
 *     behavior for Property Show, TradeList, and propertyUsersQuery.
 *
 * Do not retry:
 *   - `error.doNotRetry === true` (explicit opt-out)
 *   - HTTP 4xx other than 429 (e.g. 400, 401, 403, 404) — client error,
 *     deterministic, so retrying with the same request cannot change the
 *     outcome
 *   - any other numeric status not covered above (e.g. 1xx, 2xx, 3xx) —
 *     not an error condition this function expects to see; treated
 *     conservatively as non-retryable
 *   - GraphQL field errors (`@throwOnFieldError`, deterministic; no HTTP
 *     status, no `message === 'Request timed out'`). These arrive on HTTP 200,
 *     so retrying cannot change the outcome.
 *
 * A warning is logged for errors that fall through to the default-retryable
 * path so classification gaps are visible in production.
 * @param {Error} error - The raw error from the query layer.
 * @returns {boolean} True if the error is retryable.
 */
export const isRetryableError = (error) => {
  if (error?.doNotRetry === true) return false;

  // Timeout: message set by the internal setTimeout path in queryWithRetry.
  if (error?.message === 'Request timed out') return true;

  // HTTP status: some network layers attach a numeric status to the error.
  // Behavior per status:
  //   - 0        → retryable. Transport failure (unreachable API / fetch
  //                reject) set in environment.js; treated like a network blip.
  //   - 429      → retryable. Rate-limited / overloaded; benefits from the
  //                exponential backoff between attempts.
  //   - 500–599  → retryable. Server-side error; may be transient.
  //   - 400–428, 430–499 → non-retryable (falls through to `false` below).
  //                Client error (e.g. 400 bad request, 401 unauthorized,
  //                403 forbidden, 404 not found); deterministic, so retrying
  //                with the same request cannot change the outcome.
  //   - any other numeric status (e.g. 1xx, 2xx, 3xx) → non-retryable
  //                (falls through to `false` below). Not an error condition
  //                this function expects to see; treated conservatively as
  //                non-retryable rather than risking an infinite-seeming loop.
  const status = error?.status ?? error?.response?.status;
  if (typeof status === 'number') {
    if (status === 0 || status === 429 || status >= 500) return true;
    return false;
  }

  // Field errors arrive via @throwOnFieldError on a 200 response.
  // They carry no HTTP status, no timeout message, but the Relay error
  // object has `source.errors` with locations/path — detect this pattern.
  if (
    error?.source?.errors?.length > 0
    && !error?.networkError
  ) {
    return false;
  }

  // Unknown error: default to retryable so existing callers are not regressed.
  // No log here — many network-layer errors carry no status or source.errors,
  // and logging every one would flood production output without adding signal.
  return true;
};

/**
 * Compute the backoff delay (ms) for an attempt using full-jitter exponential
 * backoff: `random(0, min(maxDelayMs, baseDelayMs × 2^(attempt-1)))`.
 *
 * Works alongside `isRetryableError`: that function decides *if* to retry;
 * this function decides *when* — giving the backend time to recover and
 * spreading retries across a random window to avoid thundering-herd.
 *
 * The window doubles each attempt but is capped at maxDelayMs:
 *
 *   Attempt 1 → random(0,  500 ms)
 *   Attempt 2 → random(0, 1000 ms)
 *   Attempt 3 → random(0, 2000 ms)
 *   Attempt 4 → random(0, 4000 ms)
 *   Attempt 5 → random(0, 8000 ms)  ← window stops growing here (maxDelayMs)
 *   Attempt 6+ → random(0, 8000 ms)
 *
 * The actual wait is always a random value within the window, not the window
 * size itself. This prevents a "thundering herd": if many requests fail at the
 * same moment, randomness smears their retries across the window so the server
 * is not hit by all of them at the exact same millisecond.
 *
 * Returns 0 after the final attempt (nothing left to retry).
 * @param {number} attempt - Current attempt number (1-based).
 * @param {number} maxRetries - Total retry attempts.
 * @param {number} baseDelayMs - Base delay in milliseconds (window at attempt 1).
 * @param {number} maxDelayMs - Maximum window size; growth stops once reached.
 * @returns {number} Delay in milliseconds.
 */
export const computeBackoffDelay = (attempt, maxRetries, baseDelayMs, maxDelayMs) => {
  if (attempt >= maxRetries) return 0;
  const cap = Math.min(maxDelayMs, baseDelayMs * 2 ** (attempt - 1));
  return Math.random() * cap;
};

/**
 * Extracts node objects from GraphQL Relay connection edges, filtering out null values.
 * @param {object} root0 - The destructured parameter object
 * @param {Array<object>} root0.edges - Array of edge objects from a GraphQL Relay connection.
 *   Each edge should have a `node` property containing the actual data object.
 * @returns {Array<object>} Array of non-null node objects extracted from the edges.
 */
export const getNodesFromEdges = ({ edges }) => edges.map((el) => (el.node)).filter((el) => !!el);

/**
 * Generic retry wrapper for GraphQL queries with timeout handling and
 * exponential backoff with full jitter between attempts.
 *
 * Properly cancels Relay subscriptions on timeout to prevent resource leaks
 * and race conditions.
 * @param {object} options - Configuration options
 * @param {Function} options.queryFn - Function that returns a Promise with an
 *   optional `unsubscribe` method (Relay subscription pattern).
 * @param {number} options.timeoutMs - Per-attempt timeout in milliseconds.
 * @param {string} options.operationName - Name for logging purposes.
 * @param {number} [options.maxRetries=3] - Maximum attempt count (including
 *   the first attempt).
 * @param {number} [options.baseDelayMs=500] - Base delay for backoff (ms).
 * @param {number} [options.maxDelayMs=8000] - Maximum backoff delay cap (ms).
 * @returns {Promise<any>} Resolves with query result or rejects with last error.
 */
export const queryWithRetry = async ({
  queryFn,
  timeoutMs,
  operationName,
  maxRetries = 3,
  baseDelayMs = DEFAULT_BASE_DELAY_MS,
  maxDelayMs = DEFAULT_MAX_DELAY_MS,
}) => {
  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const result = await new Promise((resolve, reject) => {
        let promiseWithSubscription = null;
        let isSettled = false;

        const timeoutId = setTimeout(() => {
          if (!isSettled) {
            isSettled = true;
            logger.warn(`${operationName} timed out (attempt ${attempt}/${maxRetries})`);

            // Cancel the subscription to prevent resource leaks and race conditions
            if (promiseWithSubscription && typeof promiseWithSubscription.unsubscribe === 'function') {
              logger.log(`Cancelling subscription for ${operationName} due to timeout`);
              promiseWithSubscription.unsubscribe();
            }

            reject(new Error('Request timed out'));
          }
        }, timeoutMs);

        try {
          promiseWithSubscription = queryFn();

          // Handle the case where queryFn returns a Promise (with or without unsubscribe method)
          if (promiseWithSubscription && typeof promiseWithSubscription.then === 'function') {
            promiseWithSubscription
              .then((data) => {
                if (!isSettled) {
                  isSettled = true;
                  clearTimeout(timeoutId);
                  resolve(data);
                }
              })
              .catch((error) => {
                if (!isSettled) {
                  isSettled = true;
                  clearTimeout(timeoutId);
                  reject(error);
                }
              });
          } else if (!isSettled) {
            // If queryFn doesn't return a promise, reject immediately
            isSettled = true;
            clearTimeout(timeoutId);
            reject(new Error('queryFn must return a Promise'));
          }
        } catch (error) {
          if (!isSettled) {
            isSettled = true;
            clearTimeout(timeoutId);
            logger.error(`${operationName} failed to start (attempt ${attempt}/${maxRetries})`, error);
            reject(error);
          }
        }
      });

      return result;
    } catch (error) {
      // Classify once; use result for both the retry decision and logging.
      const retryable = isRetryableError(error);
      if (retryable) {
        // Log intermediate failures but don't trigger user-facing notifications.
        logger.error(`${operationName} failed (attempt ${attempt}/${maxRetries})`, error);
      }
      if (!retryable) {
        throw error;
      }
      lastError = error;

      if (attempt < maxRetries) {
        logger.warn(`Retrying ${operationName} (attempt ${attempt + 1}/${maxRetries})`);
        const delay = computeBackoffDelay(attempt, maxRetries, baseDelayMs, maxDelayMs);
        if (delay > 0) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => { setTimeout(resolve, delay); });
        }
      }
    }
  }

  // All retry attempts exhausted - log final error and throw
  logger.error(`${operationName} failed after ${maxRetries} attempts`, lastError);
  throw lastError;
};
